import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  API_SPECIFICATION_CONSTANTS,
  NIFTY_APPLICATIONID_KEY,
  PARTNERS_API_CONSTANTS,
  PROPERTY_KEYS,
} from '@app/app.constants';
import { environment } from '@root/environments/environment';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { Partner } from '../core.types';
import { EntityBaseService } from './entityBase.service';

@Injectable({
  providedIn: 'root',
})
export class PartnersService extends EntityBaseService<Partner> {
  protected http: HttpClient;

  constructor() {
    const http = inject(HttpClient);

    super(http);
    this.http = http;

    this.apiUrl = `${environment.apiUrl}/${PARTNERS_API_CONSTANTS.partners}`;
  }

  createDoctor(doctor: Partner): Observable<any> {
    const url = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartnerManager}/!/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandProvisionPartner}`;
    return this.http.post(url, doctor);
  }

  updateDoctor(doctor: Partner): Observable<any> {
    const url = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartnerManager}/!/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandProvisionPartnerUpdate}`;
    return this.http.post(url, doctor);
  }

  assignDoctorToPartner(partnerId: string, doctorId: string): Observable<any> {
    const url = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandAssignSubPartnerToPartner}`;
    return this.http.post(url, {
      partnerId: doctorId,
    });
  }

  unassignDoctorFromPartner(
    partnerId: string,
    doctorId: string
  ): Observable<any> {
    const url = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandUnassignSubPartnerFromPartner}`;
    return this.http.post(url, {
      partnerId: doctorId,
    });
  }

  deactivatePartner(partnerId: string) {
    const url = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandDeactivatePartner}`;
    return this.http.post(url, {});
  }

  deactivateDoctor(doctorId: string): Observable<any> {
    return this.deactivatePartner(doctorId);
  }

  activatePartner(partnerId: string): Observable<any> {
    const url = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandReactivatePartner}`;
    return this.http.post(url, {});
  }

  setProductConfiguratonToPartner(
    partnerId: string,
    productConfigurationId: string,
    isAssign: boolean
  ) {
    let productConfiguratonUrl = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/${API_SPECIFICATION_CONSTANTS.tell}`;

    if (isAssign) {
      productConfiguratonUrl = productConfiguratonUrl.concat(
        `/${PARTNERS_API_CONSTANTS.commandAssignProductConfigurationToPartner}`
      );
    } else {
      productConfiguratonUrl = productConfiguratonUrl.concat(
        `/${PARTNERS_API_CONSTANTS.commandUnassignProductConfigurationFromPartner}`
      );
    }

    const body = {
      productConfigurationId,
    };

    return this.http.post<string>(productConfiguratonUrl, body);
  }

  assignBundlesToPartner(
    partnerId: string,
    bundleIds: string[]
  ): Observable<string[]> {
    return this.http.post<string[]>(
      `${environment.apiUrl}/partners/${partnerId}/bundle-configurations`,
      {
        bundleIds,
      }
    );
  }

  unassignBundlesFromPartner(
    partnerId: string,
    bundleConfigurationIds: string[]
  ) {
    return this.http.delete<void>(
      `${environment.apiUrl}/partners/${partnerId}/bundle-configurations`,
      {
        body: {
          bundleConfigurationIds,
        },
      }
    );
  }

  assignUserAccountToPartner(partnerId: string, userAccountId: string) {
    const assignUserAccountUrl = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandAssignUserAccountToPartner}`;

    return this.http.post(assignUserAccountUrl, {
      id: userAccountId,
      provider: 'Auth0',
    });
  }

  assignNotificationProfileToPartner(
    partnerId: string,
    notificationProfileId: string
  ) {
    const assignNotificationProfileUrl =
      `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/` +
      `${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandAssignNotificationProfileToPartner}`;

    return this.http.post(assignNotificationProfileUrl, {
      notificationProfileId,
    });
  }

  unassignNotificationProfileFromPartner(
    partnerId: string,
    notificationProfileId: string
  ) {
    const unassignNotificationProfileUrl =
      `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/` +
      `${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandUnassignNotificationProfileFromPartner}`;

    return this.http.post(unassignNotificationProfileUrl, {
      notificationProfileId,
    });
  }

  findPartners(
    partnerTypesIds?: string[],
    searchString?: string,
    ids?: string[],
    roleCountries?: string[],
    accountManagerIds?: string[]
  ): Observable<Partner[]> {
    let params = new HttpParams();
    if (partnerTypesIds && partnerTypesIds.length) {
      params = params.set('partnerTypeIds', partnerTypesIds.join(','));
    }

    if (searchString) {
      params = params.set(
        API_SPECIFICATION_CONSTANTS.searchToken,
        searchString
      );
    }

    if (ids) {
      params = params.set('ids', ids.join(','));
    }

    if (roleCountries && !accountManagerIds) {
      params = params.set('AddressCountryTokens', roleCountries.join(','));
    }

    if (accountManagerIds) {
      params = params.set('accountManagerId', accountManagerIds.join(','));
    }

    return this.http.get<Partner[]>(this.getListUrl(), {
      params,
    });
  }

  getPartnersByNametoken(partnerNameToken: string) {
    return this.http.get<Partner[]>(this.getListUrl(), {
      params: new HttpParams().set('NameToken', partnerNameToken),
    });
  }

  getPartners(partnerIds: string[]): Observable<Partner[]> {
    return this.http
      .get<Partner[]>(`${environment.apiUrl}/partners`, {
        params: new HttpParams().set('Ids', partnerIds.join(',')),
      })
      .pipe(share());
  }

  getPartner(partnerId: string): Observable<Partner> {
    const getPartnersUrl =
      `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartnerProjector}/${partnerId}/${API_SPECIFICATION_CONSTANTS.ask}` +
      `/${PARTNERS_API_CONSTANTS.commandGetPartner}`;

    return this.http.get<Partner>(getPartnersUrl);
  }

  assignPaymentTypeToPartner(
    partnerId: string,
    paymentType: string,
    assign: boolean
  ) {
    let url = `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartner}/${partnerId}/${API_SPECIFICATION_CONSTANTS.tell}/`;
    if (assign) {
      url = `${url}${PARTNERS_API_CONSTANTS.commandAssignPaymentTypeToPartner}`;
    } else {
      url = `${url}${PARTNERS_API_CONSTANTS.commandUnassignPaymentTypeFromPartner}`;
    }

    return this.http.post(url, { paymentType });
  }

  getOnlinePartners(
    countries?: string[],
    accountManagerIds?: string[]
  ): Observable<string[]> {
    let params = new HttpParams()
      .set('PropertyValue', 'True')
      .set('PropertyKey', PROPERTY_KEYS.IsOnlinePartner);

    if (countries && !accountManagerIds) {
      params = params.set('AddressCountryTokens', countries?.join(','));
    }
    if (accountManagerIds) {
      params = params.set('accountManagerId', accountManagerIds.join(','));
    }

    return this.http.get<string[]>(`${environment.apiUrl}/partners/ids`, {
      params,
    });
  }

  protected setUpdateEntityBody(entity: Partner, body: Partial<Partner>) {
    return body;
  }
  protected setUrlBody(partner: Partner) {
    return partner;
  }
  protected getListUrl(): string {
    return `${environment.apiUrl}/partners?${API_SPECIFICATION_CONSTANTS.applicationId}=${NIFTY_APPLICATIONID_KEY}`;
  }
  protected getUpdateEntityUrl(entityId: string): string {
    return `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartnerManager}/!/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandProvisionPartnerUpdate}`;
  }
  protected getDeleteEntityUrl(entityId: string): string {
    throw new Error('Method not implemented.');
  }
  protected getCreateEntityUrl(newEntityId: string): string {
    return `${this.apiUrl}/${PARTNERS_API_CONSTANTS.actorPartnerManager}/!/${API_SPECIFICATION_CONSTANTS.tell}/${PARTNERS_API_CONSTANTS.commandProvisionPartner}`;
  }
  protected getFetchEntityUrl(entityId: string): string {
    throw new Error('Method not implemented.');
  }
  protected setCreateEntityBody(entity: Partner, body: Partial<Partner>) {
    return body;
  }
}
